import type { VizDataset } from "@/lib/ai/visualization/data/schema";

export type VizBarLayout = "vertical" | "horizontal";

// Recharts layout semantics:
// - layout="horizontal": X=category, Y=value (vertical bars)
// - layout="vertical":   X=value, Y=category (horizontal bars)
function isDigitChar(value: string): boolean {
  return value >= "0" && value <= "9";
}

function isIsoDatePrefix(value: string): boolean {
  const text = value.trim();
  if (text.length < 10) return false;
  const y0 = text.charAt(0);
  const y1 = text.charAt(1);
  const y2 = text.charAt(2);
  const y3 = text.charAt(3);
  const d0 = text.charAt(5);
  const d1 = text.charAt(6);
  const d2 = text.charAt(8);
  const d3 = text.charAt(9);
  if (
    !isDigitChar(y0) ||
    !isDigitChar(y1) ||
    !isDigitChar(y2) ||
    !isDigitChar(y3) ||
    !isDigitChar(d0) ||
    !isDigitChar(d1) ||
    !isDigitChar(d2) ||
    !isDigitChar(d3)
  ) {
    return false;
  }
  return text.charAt(4) === "-" && text.charAt(7) === "-";
}

/**
 * Bar chart の見やすさを優先して Recharts の layout を決定する。
 *
 * @remarks
 * ラベル長・件数・日付らしさの比率をサンプル評価し、長いカテゴリは横棒寄りに倒す。
 */
export function resolveBarLayout(args: {
  dataset: VizDataset;
  categoryKey: string;
}): VizBarLayout {
  const sample = args.dataset.rows.slice(0, 16);
  if (sample.length === 0) return "horizontal";

  let maxLabelLength = 0;
  let longLabelCount = 0;
  let dateLikeCount = 0;

  for (const row of sample) {
    const raw = row[args.categoryKey];
    const label = raw === null || raw === undefined ? "" : String(raw);
    const length = label.length;
    maxLabelLength = Math.max(maxLabelLength, length);
    if (length >= 11) longLabelCount += 1;
    if (isIsoDatePrefix(label)) dateLikeCount += 1;
  }

  const rowCount = args.dataset.rows.length;
  const longLabelRatio = longLabelCount / sample.length;
  const dateLikeRatio = dateLikeCount / sample.length;

  // Prefer horizontal bars when category labels are long or dense.
  if (maxLabelLength >= 16) return "vertical";
  if (longLabelRatio >= 0.45 && rowCount >= 6) return "vertical";
  if (rowCount >= 10 && dateLikeRatio < 0.6) return "vertical";

  // Prefer vertical bars for compact date-like categories.
  return "horizontal";
}
